import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

export const MANUAL_CREDIT_REASON_TYPES = [
  { value: 'pricing_error', label: 'Pricing Error' },
  { value: 'warranty_claim', label: 'Warranty Claim' },
  { value: 'customer_goodwill', label: 'Customer Goodwill' },
  { value: 'duplicate_payment', label: 'Duplicate Payment' },
  { value: 'service_issue', label: 'Service Issue' },
  { value: 'scope_reduction', label: 'Scope Reduction' },
  { value: 'other', label: 'Other' },
] as const;

export const ALL_CREDIT_REASON_TYPES = [
  { value: 'overpayment', label: 'Overpayment' },
  ...MANUAL_CREDIT_REASON_TYPES,
] as const;

const formSchema = z.object({
  amount: z.number({ required_error: 'Amount is required' }).positive('Amount must be greater than 0'),
  reason_type: z.string({ required_error: 'Reason type is required' }).min(1, 'Reason type is required'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
  internal_description: z.string().max(1000, 'Internal description must be 1000 characters or less').optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateManualCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { amount: number; reasonType: string; description?: string; internalDescription?: string }) => void;
  isLoading: boolean;
}

const CreateManualCreditModal: React.FC<CreateManualCreditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: undefined,
      reason_type: '',
      description: '',
      internal_description: '',
    },
  });

  const handleSubmit = (values: FormValues) => {
    onSubmit({
      amount: values.amount,
      reasonType: values.reason_type,
      description: values.description || undefined,
      internalDescription: values.internal_description || undefined,
    });
    form.reset();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Manual Credit</DialogTitle>
          <DialogDescription>
            Issue a manual credit to this customer. A credit note number will be generated automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0.00"
              {...form.register('amount', { valueAsNumber: true })}
            />
            {form.formState.errors.amount && (
              <p className="text-sm text-destructive">{form.formState.errors.amount.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason_type">Reason *</Label>
            <Select
              value={form.watch('reason_type')}
              onValueChange={(value) => form.setValue('reason_type', value, { shouldValidate: true })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_CREDIT_REASON_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.reason_type && (
              <p className="text-sm text-destructive">{form.formState.errors.reason_type.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Credit Note Description</Label>
            <Textarea
              id="description"
              placeholder="Description shown on the credit note PDF..."
              rows={2}
              {...form.register('description')}
            />
            <p className="text-xs text-muted-foreground">This will appear on the credit note document sent to the customer.</p>
            {form.formState.errors.description && (
              <p className="text-sm text-destructive">{form.formState.errors.description.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="internal_description">Internal Notes</Label>
            <Textarea
              id="internal_description"
              placeholder="Internal context for why this credit was issued..."
              rows={3}
              {...form.register('internal_description')}
            />
            <p className="text-xs text-muted-foreground">For internal use only. Not shown on credit note documents.</p>
            {form.formState.errors.internal_description && (
              <p className="text-sm text-destructive">{form.formState.errors.internal_description.message}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Credit'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateManualCreditModal;
